import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { DoctorFormData } from '../types';

const initialData: DoctorFormData = {
  firstName: '',
  lastName: '',
  facilityName: '',
  phoneNumber: '',
  streetAddress: '',
  city: '',
  state: '',
  zipCode: '',
};

// Phone number validation - accepts various formats
const phoneNumberRegex = /^[\d\s\-\(\)\+\.]+$/;

// ZIP code validation - US ZIP codes (5 digits or 5+4 format)
const zipCodeRegex = /^\d{5}(-\d{4})?$/;

// Name validation - letters, spaces, hyphens, apostrophes, periods, and commas
const nameRegex = /^[a-zA-Z\s\-'\.\,]+$/;

const getSchema = () => {
  return yup.object().shape({
    firstName: yup
      .string()
      .required('First name is required')
      .trim()
      .min(1, 'First name must be at least 1 character')
      .max(100, 'First name must be less than 100 characters')
      .matches(
        nameRegex,
        'First name can only contain letters, spaces, hyphens, apostrophes, periods, and commas',
      ),
    lastName: yup
      .string()
      .required('Last name is required')
      .trim()
      .min(1, 'Last name must be at least 1 character')
      .max(100, 'Last name must be less than 100 characters')
      .matches(
        nameRegex,
        'Last name can only contain letters, spaces, hyphens, apostrophes, periods, and commas',
      ),
    facilityName: yup
      .string()
      .nullable()
      .optional()
      .transform(value => (value === '' ? undefined : value))
      .max(200, 'Facility name must be less than 200 characters'),
    phoneNumber: yup
      .string()
      .required('Phone number is required')
      .trim()
      .min(10, 'Phone number must be at least 10 digits')
      .max(20, 'Phone number must be less than 20 characters')
      .matches(
        phoneNumberRegex,
        'Phone number can only contain digits, spaces, hyphens, parentheses, plus signs, and periods',
      )
      .test(
        'min-digits',
        'Phone number must contain at least 10 digits',
        value => {
          if (!value) return false;
          const digitsOnly = value.replace(/\D/g, '');
          return digitsOnly.length >= 10;
        },
      ),
    streetAddress: yup
      .string()
      .required('street address is required')
      .trim()
      .min(5, 'Address must be at least 5 characters')
      .max(500, 'Address must be less than 500 characters'),
    city: yup
      .string()
      .required('City is required')
      .trim()
      .min(1, 'City must be at least 1 character')
      .max(100, 'City must be less than 100 characters'),
    state: yup
      .string()
      .required('State is required')
      .trim()
      .length(2, 'State must be a 2-letter abbreviation')
      .matches(
        /^[A-Z]{2}$/,
        'State must be a valid 2-letter US state abbreviation',
      ),
    zipCode: yup
      .string()
      .required('ZIP code is required')
      .trim()
      .matches(zipCodeRegex, 'ZIP code must be in format 12345 or 12345-6789')
      .test('valid-zip', 'ZIP code must be 5 digits (or 5+4 format)', value => {
        if (!value) return false;
        const digitsOnly = value.replace(/\D/g, '');
        return digitsOnly.length === 5 || digitsOnly.length === 9;
      }),
    additionalNotes: yup
      .string()
      .nullable()
      .transform(value => (value === '' ? null : value))
      .max(1000, 'Additional notes must be less than 1000 characters'),
    doctorUuid: yup.string().uuid('Invalid doctor UUID format').optional(),
  });
};

const useDoctorForm = () => {
  const { control, handleSubmit, reset, formState, trigger, getValues } =
    useForm<DoctorFormData>({
      defaultValues: initialData,
      resolver: yupResolver(getSchema()),
      mode: 'onChange',
      reValidateMode: 'onChange',
    });

  return {
    control,
    handleSubmit,
    reset,
    isValid: formState.isValid,
    trigger,
    getValues,
    errors: formState.errors,
  };
};

export default useDoctorForm;
